import type {
  DrugEliminationNode,
  EliminationContext,
  EliminationRule,
  EliminationTrigger,
  OutcomeRef,
  PatientInput,
} from "../types";
import type { EliminationLogEntry } from "../types";

export interface EliminationAnswerPayload {
  answer: boolean | null;
  eliminatedDrugIds: string[];
  logEntries: {
    ruleId: string;
    drugIds: string[];
    reason: string;
    answer: boolean | null;
  }[];
}

export interface EliminationStepResult {
  logEntries: EliminationLogEntry[];
  eliminatedDrugIds: string[];
  remainingDrugIds: string[];
  nextTarget: string | OutcomeRef;
}

export interface ComputeEliminationAnswerArgs {
  node: DrugEliminationNode;
  answer: boolean | null;
  eligibleDrugIds: string[];
  patient?: PatientInput;
  context?: Record<string, unknown>;
}

export interface ComputedEliminationLogEntry<
  TAnswer extends boolean | null = boolean | null,
> {
  ruleId: string;
  drugIds: string[];
  reason: string;
  answer: TAnswer;
}

export interface ComputedEliminationAnswer<
  TAnswer extends boolean | null = boolean | null,
> {
  eliminatedDrugIds: string[];
  logEntries: ComputedEliminationLogEntry<TAnswer>[];
}

export function evaluateTrigger(
  trigger: EliminationTrigger,
  ctx: EliminationContext
): boolean {
  switch (trigger.kind) {
    case "always":
      return true;
    case "condition":
      if (ctx.patient === undefined || ctx.patient === null) {
        throw new Error(
          "EliminationRule condition trigger evaluated with no patient in context. " +
            "DrugEliminationNode must be supplied an evaluationPatient when any rule uses a condition trigger."
        );
      }
      return trigger.evaluate(ctx);
  }
}

export function computeEliminationAnswer(
  args: ComputeEliminationAnswerArgs & { answer: boolean }
): ComputedEliminationAnswer<boolean>;
export function computeEliminationAnswer(
  args: ComputeEliminationAnswerArgs & { answer: null }
): ComputedEliminationAnswer<null>;
export function computeEliminationAnswer({
  node,
  answer,
  eligibleDrugIds,
  patient,
  context = {},
}: ComputeEliminationAnswerArgs): ComputedEliminationAnswer {
  const eliminatedSet = new Set<string>();
  const logEntries: ComputedEliminationLogEntry[] = [];
  const seenRuleIds = new Set<string>();
  const evalCtx: EliminationContext = {
    answer,
    patient: patient as PatientInput,
    context,
    eligibleDrugIds,
  };

  const consider = (rule: EliminationRule, listAnswer: boolean) => {
    if (seenRuleIds.has(rule.id)) return;

    const fires = rule.trigger
      ? evaluateTrigger(rule.trigger, evalCtx)
      : answer !== null && listAnswer === answer;
    if (!fires) return;
    seenRuleIds.add(rule.id);

    const affected = rule.drugIds.filter((id) => eligibleDrugIds.includes(id));
    if (affected.length === 0) return;

    for (const id of affected) eliminatedSet.add(id);
    logEntries.push({
      ruleId: rule.id,
      drugIds: affected,
      reason: rule.reason,
      answer,
    });
  };

  for (const rule of node.eliminateOnYes) consider(rule, true);
  for (const rule of node.eliminateOnNo) consider(rule, false);

  return {
    eliminatedDrugIds: Array.from(eliminatedSet),
    logEntries,
  };
}

export function resolveEliminationStep(
  node: DrugEliminationNode,
  answer: EliminationAnswerPayload,
  eligibleBefore: string[],
  nodeId: string,
  now: string
): EliminationStepResult {
  const eliminatedDrugIds = answer.eliminatedDrugIds;
  const logEntries: EliminationLogEntry[] = answer.logEntries.map((e) => ({
    nodeId,
    ruleId: e.ruleId,
    drugIds: e.drugIds,
    reason: e.reason,
    answer: e.answer,
    eliminatedAt: now,
    eligibleDrugIdsBeforeNode: [...eligibleBefore],
  }));

  const remainingDrugIds = eligibleBefore.filter(
    (id) => !eliminatedDrugIds.includes(id)
  );

  const answerKey = answer.answer === null ? null : answer.answer ? "yes" : "no";
  const explicit = answerKey === null ? undefined : node.answerRouting?.[answerKey];

  let nextTarget: string | OutcomeRef;
  if (explicit !== undefined) {
    nextTarget = explicit;
  } else if (remainingDrugIds.length === 0) {
    nextTarget = node.noDrugsRemaining;
  } else {
    nextTarget = node.next;
  }

  return { logEntries, eliminatedDrugIds, remainingDrugIds, nextTarget };
}
